'use client'

import '../../mockups/_styles/editorial.css'
import { useEffect, type ReactNode } from 'react'
import Link from 'next/link'
import { ArticlePreviewCard } from '@/components/brand-kit/article-preview-card'
import type { ArticlePreview } from '@/lib/article-previews'
import { usePostHog } from 'posthog-js/react'
import { ProductShareButtons } from '@/components/blocks/product-share-buttons'
import { SafeImage } from '@/components/ui/safe-image'
import { getProductPath } from '@/lib/urls'
import { Shield, Search, ExternalLink, ArrowRight } from 'lucide-react'
import { FAQAccordion } from '@/components/brand-kit/faq-accordion'
import { Breadcrumb } from '@/components/brand-kit/breadcrumb'
import { filterHubBreadcrumbs } from '@/lib/breadcrumbs'
import { HorizontalCarousel } from '@/components/brand-kit/horizontal-carousel'
import {
  ProductCarouselCard,
  type CarouselProduct,
} from '@/components/brand-kit/product-carousel-card'
import { CTABand } from '@/components/brand-kit/cta-band'
import { formatCents } from '@/lib/format-utils'

interface FilterProduct {
  id: string
  title: string
  brand: string
  model: string
  type: string
  seo_slug: string
  normalized_model: string
  lowest_price_cents: number | null
  /** FRIDGE-1088 — the (FFN) Fridge.com Filter Number, the chip under each card title. */
  ffn?: string | null
  /** FRIDGE-1088 — the brand's same-origin logo, resolved on the server (lib/filters/brand-logos). */
  brand_logo?: string | null
  images: string[]
  features: string[]
  tags: string[]
  specs_json: Record<string, unknown> | null
}

type FilterArticle = ArticlePreview

interface FilterHub {
  slug: string
  title: string
  description: string | null
  features: Array<{ icon: string; title: string; description: string }> | null
  faqs: Array<{ question: string; answer: string }> | null
  brands_section: Array<{ name: string; features: string[]; link: string }> | null
  tools_section: Array<{ href: string; title: string; description: string }> | null
  cta_title: string | null
  cta_description: string | null
  cta_button_text: string | null
  cta_button_link: string | null
  collection_slug: string | null
}

/** Cards the filter-hub carousel shows. */
const FILTER_HUB_SLOTS = 16

function formatType(type: string): string {
  return type
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// Canonical display rule (owner ruling 07/25/2026): whole dollars drop ".00",
// real cents render exactly. Shared implementation in lib/format-utils.
function priceLabel(cents: number | null): string {
  if (!cents || cents <= 0) return ''
  return formatCents(cents)
}

export default function FilterClient({
  hub,
  products,
  articles,
  intel,
}: {
  hub: FilterHub
  products: FilterProduct[]
  articles: FilterArticle[]
  /** FRIDGE-724 — the server-rendered brand intelligence (style picker,
   *  compatibility table, model-hub rail) for brand hubs; absent elsewhere. */
  intel?: ReactNode
}) {
  const posthog = usePostHog()

  useEffect(() => {
    if (posthog) {
      posthog.capture('filter_hub_viewed', { hub_slug: hub.slug, product_count: products.length })
    }
  }, [posthog, hub.slug, products.length])

  // Split last word of hub.title for the orange italic accent (matches editorial h1 pattern).
  const titleWords = hub.title.split(' ')
  const titleHead = titleWords.length > 1 ? titleWords.slice(0, -1).join(' ') : ''
  const titleTail = titleWords.length > 1 ? titleWords[titleWords.length - 1] : hub.title

  // Map filter products → CarouselProduct shape for HorizontalCarousel.
  // Skip products without a resolvable URL or an image — ProductCarouselCard
  // always renders an <img>, so an empty src would trigger a React warning and
  // a broken-image flash.
  const carouselProducts = products
    .map((product): (CarouselProduct & { id: string }) | null => {
      const href = getProductPath(product)
      const image = product.images?.[0]
      if (!href || !image) return null
      return {
        id: product.id,
        brand: product.brand || 'Fridge.com',
        title: product.title,
        price: priceLabel(product.lowest_price_cents),
        badge: formatType(product.type) || 'Filter',
        badgeColor: 'bg-[#0469f0]',
        image,
        href,
        // FRIDGE-796 — filter URLs carry no minted number in the slug, and two
        // rows can share one manufacturer title (the Drinkpod BF-UKF8001 8-pack
        // and 10-pack both read "Maytag Refrigerator Water Filter"). The model
        // is what tells them apart; it reaches the links' accessible names only.
        identifier: product.model || undefined,
        // FRIDGE-1088 — filter cards show the brand's logo and the FFN.
        brandLogo: product.brand_logo ?? null,
        ffn: product.ffn ?? null,
      }
    })
    .filter((p): p is CarouselProduct & { id: string } => p !== null)
    // The page over-fetches (FRIDGE-398); slice back to the rail's real size
    // so the spare rows only ever REPLACE dropped ones, never add cards.
    .slice(0, FILTER_HUB_SLOTS)

  // FRIDGE-398 — the two stats below stated `products.length`, the RAW fetched
  // count, while the carousel renders only the rows that survive the href/image
  // drop above. So the page could read "16 PRODUCTS" while showing fewer: a
  // number the page cannot back with what it displays.
  //
  // Both now report what is actually rendered. A true catalogue count is a
  // DIFFERENT number and is not available here — `products` is capped by the
  // page's `.limit()`, so `products.length` was never the catalogue total
  // either; it was the fetch size. Quoting the rendered count is the honest
  // figure this component can source.
  const renderedProductCount = carouselProducts.length

  const uniqueTypeCount = new Set(
    products.map((p) => p.type).filter((t): t is string => Boolean(t))
  ).size

  return (
    <div className="ed-root">
      {/* HERO ─────────────────────────────────────────────────────────────── */}
      <section style={{ borderBottom: '1px solid #f3f4f6' }}>
        <div className="ed-container" style={{ paddingTop: 36, paddingBottom: 36 }}>
          {/* ONE trail (lib/breadcrumbs.ts) — page.tsx emits the same
              filterHubBreadcrumbs(hub) as the BreadcrumbList (FRIDGE-269). */}
          <Breadcrumb variant="editorial" trail={filterHubBreadcrumbs(hub)} style={{ marginBottom: 24 }} />

          <div className="ed-eyebrow" style={{ marginBottom: 18 }}>
            <span className="dot" />
            FILTER HUB · {renderedProductCount} PRODUCTS
            {uniqueTypeCount > 1 ? ` · ${uniqueTypeCount} TYPES` : ''}
          </div>

          <h1 className="ed-h1" style={{ fontSize: 'clamp(40px, 5vw, 72px)' }}>
            {titleHead ? `${titleHead} ` : null}
            <em style={{ fontStyle: 'italic', color: 'var(--fridge-orange)' }}>{titleTail}</em>
          </h1>
          {hub.description && (
            <p className="ed-subtitle" style={{ maxWidth: 720, fontSize: 17, marginTop: 12 }}>
              {hub.description}
            </p>
          )}

          <div className="flex flex-wrap gap-4 text-sm text-gray-700 mt-6">
            <span>&#10003; Regularly updated prices</span>
            <span>&#10003; Compare certifications</span>
            <span>&#10003; OEM + aftermarket</span>
            <span>&#10003; Model matching</span>
          </div>

          <div style={{ marginTop: 18 }}>
            <ProductShareButtons
              productTitle={hub.title}
              productUrl={`/filters/${hub.slug}`}
              variant="top"
              pageType="brand"
              customLabel="Share Fridge.com Filters:"
            />
          </div>
        </div>
      </section>

      {/* STATS ROW — 4-cell grid */}
      <section style={{ borderBottom: '1px solid #f3f4f6' }}>
        <div className="ed-container" style={{ paddingTop: 28, paddingBottom: 28 }}>
          <div className="ed-local-stats">
            <div className="cell tint">
              <div className="k">FILTERS</div>
              <div className="v">{renderedProductCount}</div>
              <div className="l">Compatible filters in this hub from the Fridge.com catalog.</div>
            </div>
            <div className="cell">
              <div className="k">CERTIFICATION</div>
              <div className="v">Check model</div>
              <div className="l">Check the exact cartridge model and its certified claims before choosing a replacement.</div>
            </div>
            <div className="cell">
              <div className="k">FILTER TYPES</div>
              <div className="v">{uniqueTypeCount || 1}</div>
              <div className="l">Distinct cartridge formats covered in this hub.</div>
            </div>
            <div className="cell">
              <div className="k">SOURCING</div>
              <div className="v">OEM+</div>
              <div className="l">OEM cartridges plus certified aftermarket equivalents.</div>
              <div className="src-credit">Source: Manufacturer &amp; retailer</div>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND INTELLIGENCE (FRIDGE-724) — style picker · compatibility table ·
          model-hub rail, server-rendered by the page for brand hubs. */}
      {intel && (
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          {intel}
        </section>
      )}

      {/* FILTER FINDER CTA — editorial info row */}
      <section style={{ borderBottom: '1px solid #f3f4f6' }}>
        <div className="ed-container" style={{ paddingTop: 32, paddingBottom: 32 }}>
          <Link
            href="/tools/refrigerator-water-filter-finder"
            className="group block bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 hover:shadow-md hover:border-[#ff9951] transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#ff9951]/10 flex items-center justify-center text-[#ff9951] flex-shrink-0">
                <Search className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-[#ff9951] block mb-1">
                  Filter Finder
                </span>
                <p className="text-base font-semibold text-gray-900">
                  Not sure which filter you need?
                </p>
                <p className="text-sm text-gray-600 mt-0.5">
                  Enter your refrigerator model number — we cross-reference OEM and certified aftermarket alternatives.
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-sm font-bold text-gray-900 group-hover:text-[#ff9951] transition-colors">
                Open finder
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
          {/* FIX-10 parity: the cost calculator was linked only from the
              deleted sr-only zone — now a visible companion to the finder. */}
          <p className="text-sm text-gray-600 mt-3">
            Budgeting replacements? Try the{' '}
            <Link href="/tools/refrigerator-water-filter-cost-calculator" className="text-[#0469f0] hover:underline font-medium">
              filter cost calculator
            </Link>
            .
          </p>
        </div>
      </section>

      {/* FEATURES (from hub.features) — quick-fact stat tiles, not reasoning */}
      {hub.features && hub.features.length > 0 && (
        <section style={{ borderBottom: '1px solid #f3f4f6', background: 'var(--gray-50)' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>At a glance</h2>
              <span className="meta">{hub.features.length} quick facts</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {hub.features.map((feature, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
                  <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="w-10 h-10 rounded-lg bg-[#0469f0]/10 flex items-center justify-center text-[#0469f0] mb-3">
                    <Shield className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-base text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRODUCTS — brand-kit HorizontalCarousel */}
      {carouselProducts.length > 0 && (
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 16, paddingBottom: 16 }}>
            <HorizontalCarousel
              eyebrow="Compatible filters"
              title={hub.title}
              description={`${carouselProducts.length} filters with regularly updated pricing from major retailers.`}
              bgClass="bg-white"
            >
              {carouselProducts.map((product) => (
                <ProductCarouselCard key={product.id} product={product} />
              ))}
            </HorizontalCarousel>
            {hub.collection_slug && (
              <div className="text-center mt-6">
                <Link
                  href={`/collections/${hub.collection_slug}`}
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#0469f0] hover:text-[#004aad]"
                >
                  View all in collection
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* FILTER BRANDS — manufacturers + verified aftermarket alternatives */}
      {hub.brands_section && hub.brands_section.length > 0 && (
        <section style={{ borderBottom: '1px solid #f3f4f6', background: 'var(--gray-50)' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Top filter brands</h2>
              <span className="meta">{hub.brands_section.length} brands · OEM &amp; certified aftermarket</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {hub.brands_section.map((brand, i) => (
                <Link
                  key={i}
                  href={brand.link}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-[#ff9951] transition-all cursor-pointer h-full flex flex-col"
                >
                  <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-semibold text-base text-gray-900 mb-2">{brand.name}</h3>
                  <ul className="text-sm text-gray-600 leading-relaxed space-y-1">
                    {brand.features.map((f, fi) => (
                      <li key={fi}>&#10003; {f}</li>
                    ))}
                  </ul>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED ARTICLES — only render cards (and the whole section) when
          the article has a real hero image. No more text-only placeholder cards. */}
      {articles.filter((a) => Boolean(a.image)).length > 0 && (
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Related articles</h2>
              <span className="meta">
                {articles.filter((a) => Boolean(a.image)).length} posts · Fridge.com editorial
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {articles.filter((a) => Boolean(a.image)).map((article) => (
                <ArticlePreviewCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* USEFUL TOOLS */}
      {hub.tools_section && hub.tools_section.length > 0 && (
        <section style={{ borderBottom: '1px solid #f3f4f6', background: 'var(--gray-50)' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Useful tools</h2>
              <span className="meta">{hub.tools_section.length} calculators + finders</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {hub.tools_section.map((tool, i) => (
                <Link
                  key={i}
                  href={tool.href}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-[#ff9951] transition-all cursor-pointer flex items-start gap-4 h-full"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#ff9951]/10 flex items-center justify-center text-[#ff9951] flex-shrink-0">
                    <Search className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="font-mono text-[10px] font-bold tracking-[0.15em] text-[#ff9951] block mb-1 uppercase">
                      Tool · {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-semibold text-base text-gray-900 mb-0.5">{tool.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{tool.description}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 mt-2" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ — brand-kit FAQAccordion */}
      {hub.faqs && hub.faqs.length > 0 && (
        <section id="faq" style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 48 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Frequently asked questions</h2>
              <span className="meta">{hub.faqs.length} {hub.faqs.length === 1 ? 'question' : 'questions'} · NSF · OEM vs aftermarket</span>
            </div>
            <FAQAccordion
              title=""
              columns={hub.faqs.length > 4 ? 2 : 1}
              defaultOpen={[0]}
              bordered={false}
              items={hub.faqs.map((f) => ({
                question: f.question,
                answer: f.answer.replace(/Fridge\.com/g, '<strong>Fridge.com</strong>'),
              }))}
            />
          </div>
        </section>
      )}

      {/* CTA BAND */}
      {hub.cta_title ? (
        <CTABand
          eyebrow="Next step"
          title={hub.cta_title}
          body={hub.cta_description || 'Find your compatible filter with the Filter Finder.'}
          buttonLabel={hub.cta_button_text || 'Find your filter'}
          buttonHref={hub.cta_button_link || '/tools/refrigerator-water-filter-finder'}
        />
      ) : (
        <CTABand
          eyebrow="Get the right filter"
          title="Not sure which filter fits your fridge?"
          body="Use the Filter Finder to match your refrigerator model number to its compatible OEM and certified aftermarket filters in seconds."
          buttonLabel="Open Filter Finder"
          buttonHref="/tools/refrigerator-water-filter-finder"
        />
      )}

      {/* Bottom share */}
      <div className="ed-container" style={{ paddingTop: 32, paddingBottom: 40 }}>
        <ProductShareButtons
          productTitle={hub.title}
          productUrl={`/filters/${hub.slug}`}
          variant="bottom"
          pageType="brand"
          customLabel="Share Fridge.com Filters:"
        />
      </div>
    </div>
  )
}
